/**
 * NotesTab — вкладка «Заметки саппорта»: textarea с автосейвом (PATCH
 * notes при потере фокуса) или пустое состояние. Заметки видны только
 * команде Skrezo (ADMIN_SPEC §3.5).
 */

import {useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {Check, StickyNote} from 'lucide-react';

import {type ClientDetail, updateClient} from '@services/clients';
import {keys} from '@queries/keys';

export const NotesTab = ({clientId, detail}: {clientId: string; detail: ClientDetail}) => {
  const [notes, setNotes] = useState(detail.notes);
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: updateClient,
    onSuccess: () => void queryClient.invalidateQueries({queryKey: keys.clients.detail(clientId)}),
  });
  /** Автосейв на потере фокуса — если текст реально поменялся */
  const handleBlur = () => {
    if (notes.trim() !== detail.notes.trim()) save.mutate({id: clientId, notes: notes.trim()});
  };

  return (
    <div className="flex flex-col gap-2.5">
      <div className="text-sm font-semibold">Заметки саппорта</div>

      {notes.trim() === '' && detail.notes === '' ? (
        <div className="flex flex-col items-center rounded-xl border border-dashed border-[#DDDFE4] bg-card px-5 py-[30px] text-center">
          <div className="mb-3 grid size-[46px] place-items-center rounded-[12px] bg-[#F0F1F4] text-skr-faint">
            <StickyNote className="size-[23px]" strokeWidth={1.7} />
          </div>
          <div className="text-[13.5px] font-semibold">Заметок ещё нет</div>
          <div className="mt-[3px] max-w-[260px] text-[12.5px] leading-[1.5] text-muted-foreground">
            Оставьте контекст по клиенту — виден только команде Skrezo.
          </div>
          <button
            onClick={() => setNotes(' ')}
            className="mt-3.5 h-9 rounded-[9px] bg-primary px-[15px] text-[13px] font-semibold text-primary-foreground"
          >
            Добавить заметку
          </button>
        </div>
      ) : (
        <>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={handleBlur}
            placeholder="Заметки видны только команде Skrezo…"
            className="min-h-[150px] w-full resize-y rounded-xl border border-input bg-card p-[13px_14px] text-[13.5px] leading-[1.5] outline-none focus:border-primary focus:ring-[3px] focus:ring-primary/12"
          />
          <div className="flex items-center gap-1.5 text-xs text-skr-faint">
            <Check className="size-[13px]" strokeWidth={2} />
            {save.isPending
              ? 'Сохраняем…'
              : 'Автосохранение при потере фокуса · видно только Skrezo'}
          </div>
        </>
      )}
    </div>
  );
};
